import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { useQueryClient } from "@tanstack/react-query";
import { notify } from "utils/notify";
import { useUpdateDevicePoolSize } from "hooks/useDevices";
import { Device } from "types/device.types";
import {
  ConfigField,
  RegionFieldRow,
} from "styles/devices/deviceConfig.styles";

interface PoolSizeAdminFieldProps {
  serialNumber: string;
  linkId?: string;
  currentPoolSize?: number;
}

export function PoolSizeAdminField({
  serialNumber,
  linkId,
  currentPoolSize,
}: PoolSizeAdminFieldProps) {
  const queryClient = useQueryClient();
  const updatePoolSize = useUpdateDevicePoolSize();
  const [poolSize, setPoolSize] = useState(currentPoolSize ?? 0);

  useEffect(() => {
    if (typeof currentPoolSize === "number") setPoolSize(currentPoolSize);
  }, [currentPoolSize]);

  const savePoolSize = () => {
    if (!Number.isSafeInteger(poolSize) || poolSize <= 0) {
      notify.error("Pool volume must be a positive whole number");
      return;
    }

    updatePoolSize.mutate(
      { serialNumber, poolSize },
      {
        onSuccess: (response) => {
          queryClient.setQueryData(
            ["getDevice", serialNumber, linkId ?? null],
            (oldData: Device | undefined) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                poolSize: response.data?.poolSize ?? poolSize,
              };
            },
          );
          notify.success("Pool volume updated successfully");
        },
        onError: (error) => {
          console.error("Failed to update pool volume:", error);
          notify.error("Failed to update pool volume");
        },
      },
    );
  };

  const isUnchanged =
    typeof currentPoolSize === "number" && poolSize === currentPoolSize;

  return (
    <ConfigField $width="18rem">
      <label htmlFor="poolVolume">Pool Volume</label>
      <RegionFieldRow>
        <InputNumber
          id="poolVolume"
          value={poolSize || null}
          onValueChange={(event) => setPoolSize(event.value ?? 0)}
          min={1}
          maxFractionDigits={0}
          useGrouping
          suffix=" L"
          disabled={updatePoolSize.isPending}
        />
        <Button
          label="Save"
          icon="pi pi-save"
          onClick={savePoolSize}
          loading={updatePoolSize.isPending}
          disabled={updatePoolSize.isPending || isUnchanged}
        />
      </RegionFieldRow>
      <small>Pool volume in liters (admin only)</small>
    </ConfigField>
  );
}
